import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, ControlOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Dayjs } from 'dayjs';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { BudgetExceedanceRequestModal } from '@/components/project/BudgetExceedanceRequestModal';
import { BudgetCodeLabel } from '@/components/project/BudgetCodeLabel';
import { PrintableSubcontractDocument } from '@/components/epc/PrintableSubcontractAgreement';
import { ConvertCertificationToSiModal } from '@/components/epc/ConvertCertificationToSiModal';
import api from '@/services/api.service';
import { epcSubcontractAdminService } from '@/services/epcSubcontractAdmin.service';
import { projectService } from '@/services/project.service';
import { ENTITY_TYPE_SUBCONTRACTOR_AGREEMENT } from '@/constants/approval-entity-types';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';

const { Title, Text } = Typography;

const CLAUSE_PROFILE_OPTIONS = [
  'CUSTOM',
  'FIDIC_RED_BOOK',
  'FIDIC_YELLOW_BOOK',
  'FIDIC_SILVER_BOOK',
  'FIDIC_GREEN_BOOK',
  'CLIENT_STANDARD',
  'COMPANY_STANDARD',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const OBLIGATION_STATUS_OPTIONS = [
  'NOT_STARTED',
  'UPCOMING',
  'IN_PROGRESS',
  'SUBMITTED',
  'AWAITING_CLIENT',
  'COMPLETED',
  'OVERDUE',
  'WAIVED',
  'SUPERSEDED',
  'DISPUTED',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const SECURITY_TYPE_OPTIONS = [
  'BID_BOND',
  'PERFORMANCE_BOND',
  'ADVANCE_PAYMENT_GUARANTEE',
  'RETENTION_BOND',
  'WARRANTY_BOND',
  'PARENT_COMPANY_GUARANTEE',
  'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const INSURANCE_TYPE_OPTIONS = [
  'CAR_EAR',
  'THIRD_PARTY_LIABILITY',
  'PROFESSIONAL_INDEMNITY',
  'WORKERS_COMPENSATION',
  'MARINE_CARGO',
  'PLANT_EQUIPMENT',
  'MOTOR',
  'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

function SimpleTable({
  rows,
  columns,
}: {
  rows: Record<string, unknown>[];
  columns: {
    title: string;
    dataIndex: string;
    width?: number;
    render?: (v: unknown, r: Record<string, unknown>) => React.ReactNode;
  }[];
}) {
  if (!rows.length) return <Empty description="No records" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <Table
      size="small"
      rowKey={(r) => String(r.id || JSON.stringify(r))}
      dataSource={rows}
      pagination={false}
      scroll={{ x: true }}
      columns={columns.map((c) => ({
        ...c,
        render: c.render || ((v: unknown) => (v == null || v === '' ? '—' : String(v))),
      }))}
    />
  );
}

function money(v: unknown, currency?: unknown) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const cur = currency ? `${currency} ` : '';
  return `${cur}${n.toLocaleString()}`;
}

function apiErr(e: unknown): { code?: string; message: string } {
  const ax = e as {
    response?: { data?: { message?: string; errors?: { code?: string }; code?: string } };
    message?: string;
  };
  const data = ax?.response?.data;
  return {
    code: data?.errors?.code || data?.code,
    message: data?.message || ax?.message || 'Request failed',
  };
}

function buildAgreementSpendLines(agreement: Record<string, unknown>) {
  const projectId = agreement.project_id ? String(agreement.project_id) : '';
  const lines = Array.isArray(agreement.lines) ? (agreement.lines as Record<string, unknown>[]) : [];
  const costCategory = String(agreement.cost_category || 'Subcontract');
  if (lines.length) {
    return lines.map((ln) => ({
      projectId,
      wbsNodeId: ln.wbs_node_id || agreement.wbs_node_id || null,
      cbsCodeId: ln.cbs_code_id || agreement.cbs_code_id || null,
      costCategory: String(ln.cost_category || costCategory),
      amount: Number(ln.amount) || 0,
      sourceLineId: ln.id ? String(ln.id) : undefined,
    }));
  }
  return [
    {
      projectId,
      wbsNodeId: agreement.wbs_node_id || null,
      cbsCodeId: agreement.cbs_code_id || null,
      costCategory,
      amount: Number(agreement.contract_sum) || 0,
      sourceLineId: agreement.id ? String(agreement.id) : undefined,
    },
  ];
}

function ymd(d: Dayjs | null | undefined): string | undefined {
  return d ? d.format('YYYY-MM-DD') : undefined;
}

const EpcSubcontractWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const qc = useQueryClient();

  const [exceedanceOpen, setExceedanceOpen] = useState(false);
  const [exceedanceProjectId, setExceedanceProjectId] = useState('');
  const [exceedanceSourceType, setExceedanceSourceType] = useState('subcontractor_agreement');
  const [exceedanceSourceId, setExceedanceSourceId] = useState('');
  const [exceedanceLines, setExceedanceLines] = useState<Array<Record<string, unknown>>>([]);
  const [exceedanceCheck, setExceedanceCheck] = useState<Record<string, unknown> | null>(null);
  const [cepHints, setCepHints] = useState<Record<string, unknown>[] | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [oblOpen, setOblOpen] = useState(false);
  const [voOpen, setVoOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [eotOpen, setEotOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);
  const [convertCertId, setConvertCertId] = useState<string | null>(null);

  const [editForm] = Form.useForm();
  const [oblForm] = Form.useForm();
  const [voForm] = Form.useForm();
  const [claimForm] = Form.useForm();
  const [eotForm] = Form.useForm();
  const [secForm] = Form.useForm();
  const [insForm] = Form.useForm();

  const wsQ = useQuery({
    queryKey: ['epc-subcontract-workspace', id],
    queryFn: () => epcSubcontractAdminService.getWorkspace(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const eotQ = useQuery({
    queryKey: ['epc-subcontract-eot', id],
    queryFn: () => epcSubcontractAdminService.listEotRequests(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const faQ = useQuery({
    queryKey: ['epc-subcontract-final-accounts', id],
    queryFn: () => epcSubcontractAdminService.listFinalAccounts(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const closeQ = useQuery({
    queryKey: ['epc-subcontract-closeouts', id],
    queryFn: () => epcSubcontractAdminService.listCloseouts(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const ldQ = useQuery({
    queryKey: ['epc-subcontract-ld', id],
    queryFn: () => epcSubcontractAdminService.getLdExposure(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
  const riskQ = useQuery({
    queryKey: ['epc-subcontract-commitment-risk', id],
    queryFn: () => epcSubcontractAdminService.getCommitmentRisk(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  const agreement = wsQ.data?.agreement;
  const projectId = agreement?.project_id ? String(agreement.project_id) : '';
  const agrStatus = String(agreement?.status || '');
  const canEditParticulars = agrStatus === 'Draft' || agrStatus === 'Rejected';
  const isActive = agrStatus === 'Active' || agrStatus === 'Approved';

  const softCostQ = useQuery({
    queryKey: ['epc-subcontract-cost-check', id, projectId],
    queryFn: async () => {
      const lines = buildAgreementSpendLines(agreement!);
      return projectService.checkCostControl(projectId, {
        lines,
        sourceEntityType: 'subcontractor_agreement',
        sourceEntityId: id!,
        excludeSourceType: 'subcontractor_agreement',
        excludeSourceId: id!,
      });
    },
    enabled: Boolean(id && projectId && agreement),
    staleTime: 20_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontracts'] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-cost-check', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-eot', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-final-accounts', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-closeouts', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-ld', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-control-tower', id] });
    qc.invalidateQueries({ queryKey: ['epc-subcontract-forecast', id] });
  };

  const openExceedance = async (opts: {
    sourceEntityType: string;
    sourceEntityId: string;
    lines: Array<Record<string, unknown>>;
    projectId: string;
  }) => {
    setExceedanceSourceType(opts.sourceEntityType);
    setExceedanceSourceId(opts.sourceEntityId);
    setExceedanceLines(opts.lines);
    setExceedanceProjectId(opts.projectId);
    try {
      const check = await projectService.checkCostControl(opts.projectId, {
        lines: opts.lines,
        sourceEntityType: opts.sourceEntityType,
        sourceEntityId: opts.sourceEntityId,
        excludeSourceType: opts.sourceEntityType,
        excludeSourceId: opts.sourceEntityId,
      });
      setExceedanceCheck(check as Record<string, unknown>);
    } catch {
      setExceedanceCheck(null);
    }
    setExceedanceOpen(true);
  };

  const submitAgrM = useMutation({
    mutationFn: () => epcSubcontractAdminService.submitAgreement(id!),
    onSuccess: () => {
      message.success('Agreement submitted');
      invalidate();
    },
    onError: async (e: unknown) => {
      const { code, message: msg } = apiErr(e);
      if (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(msg)) {
        message.warning(msg);
        if (agreement && projectId) {
          await openExceedance({
            sourceEntityType: 'subcontractor_agreement',
            sourceEntityId: id!,
            lines: buildAgreementSpendLines(agreement),
            projectId,
          });
        }
      } else {
        message.error(msg);
      }
    },
  });

  const patchAgrM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.patchAgreement(id!, payload),
    onSuccess: () => {
      message.success('Agreement updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createOblM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createObligation(id!, payload),
    onSuccess: () => {
      message.success('Obligation created');
      setOblOpen(false);
      oblForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createCertM = useMutation({
    mutationFn: (paymentCertificateId: string) =>
      epcSubcontractAdminService.createCertification({ paymentCertificateId }),
    onSuccess: () => {
      message.success('Certification created from SPC');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const submitVoM = useMutation({
    mutationFn: (voId: string) => epcSubcontractAdminService.submitVariation(voId),
    onSuccess: () => {
      message.success('Variation submitted');
      invalidate();
    },
    onError: async (e: unknown, voId: string) => {
      const { code, message: msg } = apiErr(e);
      if (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(msg)) {
        message.warning(msg);
        const vo = (wsQ.data?.variations || []).find((v) => String(v.id) === String(voId));
        if (agreement && projectId && vo) {
          await openExceedance({
            sourceEntityType: 'subcontract_variation',
            sourceEntityId: String(voId),
            lines: [
              {
                projectId,
                wbsNodeId: agreement.wbs_node_id || null,
                cbsCodeId: agreement.cbs_code_id || null,
                costCategory: String(agreement.cost_category || 'Subcontract'),
                amount: Number(vo.proposed_value) || 0,
                sourceLineId: String(voId),
              },
            ],
            projectId,
          });
        }
      } else {
        message.error(msg);
      }
    },
  });

  const createVoM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createVariation(payload),
    onSuccess: () => {
      message.success('Variation created');
      setVoOpen(false);
      voForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createClaimM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createClaim(payload),
    onSuccess: () => {
      message.success('Claim created');
      setClaimOpen(false);
      claimForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const submitClaimM = useMutation({
    mutationFn: (claimId: string) => epcSubcontractAdminService.submitClaim(claimId),
    onSuccess: () => {
      message.success('Claim submitted');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createEotM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createEotRequest(payload),
    onSuccess: () => {
      message.success('EOT request created');
      setEotOpen(false);
      eotForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const submitEotM = useMutation({
    mutationFn: (eotId: string) => epcSubcontractAdminService.submitEotRequest(eotId),
    onSuccess: () => {
      message.success('EOT submitted');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createSecM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createSecurity(id!, payload),
    onSuccess: () => {
      message.success('Security created');
      setSecOpen(false);
      secForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createInsM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcSubcontractAdminService.createInsurance(id!, payload),
    onSuccess: () => {
      message.success('Insurance created');
      setInsOpen(false);
      insForm.resetFields();
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createFaM = useMutation({
    mutationFn: () => epcSubcontractAdminService.createFinalAccount({ agreementId: id }),
    onSuccess: () => {
      message.success('Final account created');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const createCloseM = useMutation({
    mutationFn: () => epcSubcontractAdminService.createCloseout({ agreementId: id }),
    onSuccess: () => {
      message.success('Closeout created');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const submitSpcM = useMutation({
    mutationFn: (spcId: string) => epcSubcontractAdminService.submitPaymentCertificate(spcId),
    onSuccess: () => {
      message.success('SPC submitted');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const confirmCertM = useMutation({
    mutationFn: (certId: string) => epcSubcontractAdminService.confirmCertification(certId),
    onSuccess: () => {
      message.success('Certification confirmed');
      invalidate();
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const convertSiM = useMutation({
    mutationFn: (args: {
      certId: string;
      departmentId: string;
      expenseAccountId: string;
      vatConfigurationId?: string | null;
      inputVatAccountId?: string | null;
      applyVat: boolean;
    }) =>
      epcSubcontractAdminService.convertCertificationToSupplierInvoice(args.certId, {
        departmentId: args.departmentId,
        expenseAccountId: args.expenseAccountId,
        vatConfigurationId: args.vatConfigurationId,
        inputVatAccountId: args.inputVatAccountId,
        applyVat: args.applyVat,
      }),
    onSuccess: (data) => {
      message.success('Draft Supplier Invoice ready');
      setConvertCertId(null);
      invalidate();
      const row = data as Record<string, unknown>;
      const bridge = row.supplierInvoice as Record<string, unknown> | undefined;
      const cert = row.certification as Record<string, unknown> | undefined;
      const siId =
        bridge?.supplierInvoiceId ||
        cert?.supplier_invoice_id ||
        row.supplierInvoiceId ||
        row.id;
      if (siId) navigate(`/fin/direct-purchase-invoices/${String(siId)}`);
    },
    onError: (e: unknown) => message.error(apiErr(e).message),
  });

  const applyCepM = useMutation({
    mutationFn: async () => {
      const hints = await epcSubcontractAdminService.getCepProgressHints(id!);
      setCepHints(hints.lines || []);
      return hints;
    },
    onSuccess: (hints) => {
      if (!hints.available && !(hints.lines || []).length) {
        message.warning(hints.note || hints.reason || 'CEP hints unavailable');
        return;
      }
      navigate(`/epc/commercial/subcontracts/${id}/certificates/new?cep=1`);
    },
    onError: (e: unknown) => message.error(apiErr(e).message || (e as Error).message),
  });

  const doubleRisk = useMemo(() => {
    const risks = (riskQ.data?.risks as unknown[]) || [];
    return Array.isArray(risks) && risks.length > 0;
  }, [riskQ.data]);

  if (!id) return null;

  if (wsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (wsQ.isError || !wsQ.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message={(wsQ.error as Error)?.message || 'Failed to load subcontract workspace'}
        />
      </div>
    );
  }

  const {
    agreement: agr,
    obligations,
    securities,
    insurance,
    paymentCertificates,
    certifications,
    variations,
    claims,
    forecast,
  } = wsQ.data;
  const agreementId = String(agr.id);
  const currency = agr.currency;
  const certBySpcId = new Set(
    (certifications || [])
      .map((c) => String(c.payment_certificate_id || c.paymentCertificateId || ''))
      .filter(Boolean)
  );

  const setTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const printAgreement = async () => {
    try {
      // Always reload agreement so print includes activity line descriptions
      const full = await epcSubcontractAdminService.getAgreement(agreementId);
      const supplierId = String(full.supplier_id || agr.supplier_id || '');
      let supplierName = String(full.supplier_name || agr.supplier_name || '');
      if (!supplierName && supplierId) {
        try {
          const res = await api.get(`/procurement/suppliers/${supplierId}`);
          const body = (res as { data?: unknown })?.data ?? res;
          const s = ((body as { data?: unknown })?.data ?? body) as Record<string, unknown>;
          supplierName = String(s.supplier_name || s.name || '');
        } catch {
          /* optional enrichment */
        }
      }
      let projectLabel = '';
      const pid = String(full.project_id || agr.project_id || '');
      if (pid) {
        try {
          const p = await projectService.getProjectById(pid);
          const pn = (p as { project_number?: string; project_name?: string }) || {};
          projectLabel = pn.project_number
            ? `${pn.project_number}${pn.project_name ? ` — ${pn.project_name}` : ''}`
            : String(pn.project_name || '');
        } catch {
          /* optional */
        }
      }
      openPmvPrintOverlay((onClose) => (
        <PrintableSubcontractDocument
          kind="agreement"
          agreement={full}
          supplierName={supplierName || undefined}
          projectLabel={projectLabel || undefined}
          onClose={onClose}
        />
      ));
    } catch (e: unknown) {
      message.error(apiErr(e).message);
    }
  };

  const printCertificate = async (spcId: string) => {
    try {
      const cert = await epcSubcontractAdminService.getPaymentCertificate(spcId);
      let supplierName = String(agr.supplier_name || '');
      const supplierId = String(agr.supplier_id || '');
      if (!supplierName && supplierId) {
        try {
          const res = await api.get(`/procurement/suppliers/${supplierId}`);
          const body = (res as { data?: unknown })?.data ?? res;
          const s = ((body as { data?: unknown })?.data ?? body) as Record<string, unknown>;
          supplierName = String(s.supplier_name || s.name || '');
        } catch {
          /* optional */
        }
      }
      openPmvPrintOverlay((onClose) => (
        <PrintableSubcontractDocument
          kind="certificate"
          certificate={cert}
          agreement={agr}
          supplierName={supplierName || undefined}
          onClose={onClose}
        />
      ));
    } catch (e: unknown) {
      message.error(apiErr(e).message);
    }
  };

  const printFinalAccount = (fa: Record<string, unknown>) => {
    openPmvPrintOverlay((onClose) => (
      <PrintableSubcontractDocument
        kind="final_account"
        finalAccount={fa}
        agreement={agr}
        onClose={onClose}
      />
    ));
  };

  const openEditDrawer = () => {
    editForm.setFieldsValue({
      title: agr.title,
      contractSum: agr.contract_sum != null ? Number(agr.contract_sum) : undefined,
      advancePct: agr.advance_pct != null ? Number(agr.advance_pct) : undefined,
      retentionPct: agr.retention_pct != null ? Number(agr.retention_pct) : undefined,
      attendancePct: agr.attendance_pct != null ? Number(agr.attendance_pct) : undefined,
      ldRatePerDay: agr.ld_rate_per_day != null ? Number(agr.ld_rate_per_day) : undefined,
      ldCap: agr.ld_cap != null ? Number(agr.ld_cap) : undefined,
      clauseProfile: agr.clause_profile || agr.clauseProfile || 'CUSTOM',
    });
    setEditOpen(true);
  };

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {doubleRisk ? (
            <Alert
              type="warning"
              showIcon
              message="Possible double commitment"
              description="Active SCA commitment overlaps child PO Subcontract lines. Material/Equipment PO lines may still commit; Subcontract lines under this SCA should not."
            />
          ) : null}
          {softCostQ.data && (softCostQ.data as { requiresExceedance?: boolean }).requiresExceedance ? (
            <Alert
              type="warning"
              showIcon
              message="Budget exceedance likely required before submit"
              action={
                <Button
                  size="small"
                  onClick={() =>
                    openExceedance({
                      sourceEntityType: 'subcontractor_agreement',
                      sourceEntityId: agreementId,
                      lines: buildAgreementSpendLines(agr),
                      projectId,
                    })
                  }
                >
                  Request exceedance
                </Button>
              }
            />
          ) : null}
          <Space wrap>
            <Button icon={<PrinterOutlined />} onClick={printAgreement}>
              Print agreement
            </Button>
            {canEditParticulars ? (
              <>
                <Button onClick={() => navigate(`/epc/commercial/subcontracts/${agreementId}/edit`)}>
                  Edit agreement
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => setOblOpen(true)}>
                  New Obligation
                </Button>
              </>
            ) : null}
          </Space>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Agreement #">
              {String(agr.agreement_number || '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Title">{String(agr.title || '—')}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag>{String(agr.status || '—')}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Clause profile">
              {String(agr.clauseProfile || agr.clause_profile || '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">{String(currency || '—')}</Descriptions.Item>
            <Descriptions.Item label="Contract sum">
              {money(agr.contract_sum, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="LD exposure (advisory)">
              {ldQ.data
                ? money(ldQ.data.exposureAmount, ldQ.data.currency || currency)
                : forecast?.ldExposureAdvisory != null
                  ? money(forecast.ldExposureAdvisory, forecast.currency)
                  : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Control tower">
              <Link to={`/epc/commercial/subcontracts/${agreementId}/control`}>
                Open commercial control
              </Link>
            </Descriptions.Item>
            <Descriptions.Item label="Payment vouchers">
              <Link to="/fin/payment-vouchers">Finance PV register</Link>
            </Descriptions.Item>
          </Descriptions>
          <div>
            <Space style={{ marginBottom: 8 }} wrap>
              <Text strong>Agreement lines</Text>
              {canEditParticulars ? (
                <Button
                  size="small"
                  onClick={() => navigate(`/epc/commercial/subcontracts/${agreementId}/edit`)}
                >
                  Edit lines
                </Button>
              ) : null}
            </Space>
            <SimpleTable
              rows={Array.isArray(agr.lines) ? (agr.lines as Record<string, unknown>[]) : []}
              columns={[
                { title: 'Code', dataIndex: 'line_code', width: 100 },
                { title: 'Description', dataIndex: 'description' },
                {
                  title: 'Qty',
                  dataIndex: 'qty',
                  width: 80,
                  render: (v: unknown) => (v != null ? String(v) : '—'),
                },
                {
                  title: 'Rate',
                  dataIndex: 'unit_rate',
                  width: 110,
                  render: (v: unknown) => money(v, currency),
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  width: 120,
                  render: (v: unknown) => money(v, currency),
                },
                {
                  title: 'Category',
                  dataIndex: 'cost_category',
                  width: 110,
                  render: (v: unknown) => String(v || '—'),
                },
                {
                  title: 'WBS',
                  dataIndex: 'wbs_node_id',
                  width: 160,
                  render: (v: unknown) =>
                    v && projectId ? (
                      <BudgetCodeLabel projectId={projectId} nodeId={String(v)} kind="wbs" />
                    ) : (
                      '—'
                    ),
                },
              ]}
            />
          </div>
          {forecast ? (
            <Descriptions bordered size="small" column={2} title="Forecast (advisory)">
              <Descriptions.Item label="Certified to date">
                {money(forecast.certifiedToDate, forecast.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Retention held">
                {money(forecast.retentionHeld, forecast.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Claims open">
                {money(forecast.claimsOpen, forecast.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Projected final">
                {money(forecast.projectedFinal, forecast.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="LD exposure">
                {money(forecast.ldExposureAdvisory ?? 0, forecast.currency)}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          <div>
            <Space style={{ marginBottom: 8 }} wrap>
              <Text strong>Obligations</Text>
              {canEditParticulars ? (
                <Button size="small" icon={<PlusOutlined />} onClick={() => setOblOpen(true)}>
                  New
                </Button>
              ) : null}
            </Space>
            <SimpleTable
              rows={obligations}
              columns={[
                { title: 'Title', dataIndex: 'title' },
                { title: 'Due', dataIndex: 'due_date', width: 120 },
                { title: 'Status', dataIndex: 'status', width: 120 },
              ]}
            />
          </div>
          <EntityAttachmentsCard entityType="SubcontractorAgreement" entityId={agreementId} />
          <ApprovalPanel entityType={ENTITY_TYPE_SUBCONTRACTOR_AGREEMENT} entityId={agreementId} />
        </Space>
      ),
    },
    {
      key: 'certificates',
      label: 'Certificates',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/epc/commercial/subcontracts/${agreementId}/certificates/new`)}
            >
              New SPC
            </Button>
            <Button loading={applyCepM.isPending} onClick={() => applyCepM.mutate()}>
              New SPC from CEP
            </Button>
            <Text type="secondary">Opens the certificate page with agreement lines (CEP optional).</Text>
          </Space>
          {cepHints?.length ? (
            <SimpleTable
              rows={cepHints}
              columns={[
                { title: 'Line', dataIndex: 'lineCode', width: 100 },
                { title: 'Description', dataIndex: 'description' },
                { title: 'Progress %', dataIndex: 'progressPct', width: 100 },
                { title: 'Suggested qty', dataIndex: 'suggestedQty', width: 120 },
                {
                  title: 'Suggested amt',
                  dataIndex: 'suggestedAmount',
                  width: 120,
                  render: (v) => money(v, currency),
                },
              ]}
            />
          ) : null}
          <div>
            <Text strong>Payment certificates (SPC)</Text>
            <SimpleTable
              rows={paymentCertificates}
              columns={[
                {
                  title: 'Number',
                  dataIndex: 'certificate_number',
                  width: 140,
                  render: (v, r) => (
                    <Link to={`/epc/commercial/subcontracts/${agreementId}/certificates/${r.id}`}>
                      {String(v || r.id)}
                    </Link>
                  ),
                },
                { title: 'Kind', dataIndex: 'certificate_kind', width: 120 },
                {
                  title: 'Period',
                  dataIndex: 'period_from',
                  width: 180,
                  render: (_v, r) => {
                    const from = r.period_from ? String(r.period_from).slice(0, 10) : '';
                    const to = r.period_to ? String(r.period_to).slice(0, 10) : '';
                    if (!from && !to) return '—';
                    return `${from || '…'} → ${to || '…'}`;
                  },
                },
                {
                  title: 'Gross',
                  dataIndex: 'gross_applied_amount',
                  width: 120,
                  render: (v) => money(v, currency),
                },
                {
                  title: 'Net applied',
                  dataIndex: 'net_applied_amount',
                  width: 140,
                  render: (v) => money(v, currency),
                },
                { title: 'Status', dataIndex: 'status', width: 120 },
                {
                  title: 'Actions',
                  dataIndex: 'id',
                  width: 320,
                  render: (_v, r) => {
                    const st = String(r.status);
                    const hasCert = certBySpcId.has(String(r.id));
                    return (
                      <Space size="small" wrap>
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/epc/commercial/subcontracts/${agreementId}/certificates/${r.id}`
                            )
                          }
                        >
                          Open
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          icon={<PrinterOutlined />}
                          onClick={() => printCertificate(String(r.id))}
                        >
                          Print
                        </Button>
                        {st === 'Draft' ? (
                          <Button
                            type="link"
                            size="small"
                            loading={submitSpcM.isPending}
                            onClick={() => submitSpcM.mutate(String(r.id))}
                          >
                            Submit
                          </Button>
                        ) : null}
                        {(st === 'Approved' || st === 'Submitted') && !hasCert ? (
                          <Button
                            type="link"
                            size="small"
                            loading={createCertM.isPending}
                            onClick={() => createCertM.mutate(String(r.id))}
                          >
                            Create certification
                          </Button>
                        ) : null}
                      </Space>
                    );
                  },
                },
              ]}
            />
          </div>
          <div>
            <Text strong>Certifications</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Confirm → convert creates Draft Direct PI only. Submit / Approve / Post in Finance →
              Direct purchase invoices, then allocate on a Payment voucher.
            </Text>
            <SimpleTable
              rows={certifications}
              columns={[
                { title: 'Number', dataIndex: 'certification_number', width: 140 },
                {
                  title: 'Certified net',
                  dataIndex: 'certified_net_amount',
                  width: 140,
                  render: (v) => money(v, currency),
                },
                { title: 'Status', dataIndex: 'status', width: 120 },
                {
                  title: 'Supplier invoice',
                  dataIndex: 'supplier_invoice_id',
                  width: 180,
                  render: (_v, r) => {
                    const siId = r.supplier_invoice_id
                      ? String(r.supplier_invoice_id)
                      : '';
                    if (!siId) return '—';
                    const docNo =
                      String(
                        r.supplier_invoice_doc_number ||
                          r.supplier_invoice_number ||
                          r.invoice_number ||
                          ''
                      ).trim() || siId.slice(0, 8) + '…';
                    const status = r.supplier_invoice_status
                      ? String(r.supplier_invoice_status)
                      : '';
                    return (
                      <Link to={`/fin/direct-purchase-invoices/${siId}`}>
                        {docNo}
                        {status ? ` (${status})` : ''}
                      </Link>
                    );
                  },
                },
                {
                  title: 'Actions',
                  dataIndex: 'id',
                  width: 260,
                  render: (_v, r) => (
                    <Space size="small">
                      {String(r.status) !== 'Certified' ? (
                        <Button
                          type="link"
                          size="small"
                          loading={confirmCertM.isPending}
                          onClick={() => confirmCertM.mutate(String(r.id))}
                        >
                          Certify
                        </Button>
                      ) : null}
                      {String(r.status) === 'Certified' && !r.supplier_invoice_id ? (
                        <Button
                          type="link"
                          size="small"
                          loading={convertSiM.isPending && convertCertId === String(r.id)}
                          onClick={() => setConvertCertId(String(r.id))}
                        >
                          Convert SI
                        </Button>
                      ) : null}
                      {r.supplier_invoice_id ? (
                        <Link
                          to={`/fin/direct-purchase-invoices/${String(r.supplier_invoice_id)}`}
                        >
                          Open Direct PI
                        </Link>
                      ) : null}
                    </Space>
                  ),
                },
              ]}
            />
          </div>
        </Space>
      ),
    },
    {
      key: 'variations',
      label: 'Variations',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setVoOpen(true)}>
            New Variation
          </Button>
          <SimpleTable
            rows={variations}
            columns={[
              { title: 'Number', dataIndex: 'variation_number', width: 140 },
              { title: 'Title', dataIndex: 'title' },
              {
                title: 'Proposed',
                dataIndex: 'proposed_value',
                width: 140,
                render: (v) => money(v, currency),
              },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: 'Actions',
                dataIndex: 'id',
                width: 100,
                render: (_v, r) =>
                  String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
                    <Button
                      type="link"
                      size="small"
                      loading={submitVoM.isPending}
                      onClick={() => submitVoM.mutate(String(r.id))}
                    >
                      Submit
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'claims',
      label: 'Claims',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setClaimOpen(true)}>
            New Claim
          </Button>
          <SimpleTable
            rows={claims}
            columns={[
              { title: 'Number', dataIndex: 'claim_number', width: 140 },
              { title: 'Title', dataIndex: 'title' },
              { title: 'Direction', dataIndex: 'claim_direction', width: 120 },
              {
                title: 'Claimed',
                dataIndex: 'claimed_amount',
                width: 140,
                render: (v) => money(v, currency),
              },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: 'Actions',
                dataIndex: 'id',
                width: 100,
                render: (_v, r) =>
                  String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
                    <Button
                      type="link"
                      size="small"
                      loading={submitClaimM.isPending}
                      onClick={() => submitClaimM.mutate(String(r.id))}
                    >
                      Submit
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'eot',
      label: 'EOT',
      children: eotQ.isError ? (
        <Alert type="warning" showIcon message={(eotQ.error as Error)?.message} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEotOpen(true)}>
            New EOT
          </Button>
          <SimpleTable
            rows={(eotQ.data || []) as Record<string, unknown>[]}
            columns={[
              { title: 'Number', dataIndex: 'eot_number', width: 140 },
              {
                title: 'Days requested',
                dataIndex: 'requested_days',
                width: 120,
                render: (v, r) => String(v ?? r.days_requested ?? '—'),
              },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: 'Actions',
                dataIndex: 'id',
                width: 100,
                render: (_v, r) =>
                  String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
                    <Button
                      type="link"
                      size="small"
                      loading={submitEotM.isPending}
                      onClick={() => submitEotM.mutate(String(r.id))}
                    >
                      Submit
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'securities',
      label: 'Securities',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setSecOpen(true)}>
              New Security
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => setInsOpen(true)}>
              New Insurance
            </Button>
          </Space>
          <div>
            <Text strong>Bonds & guarantees</Text>
            <SimpleTable
              rows={securities}
              columns={[
                { title: 'Type', dataIndex: 'security_type', width: 140 },
                { title: 'Ref', dataIndex: 'reference_number', width: 140 },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  width: 140,
                  render: (v, r) => money(v, r.currency || currency),
                },
                { title: 'Expiry', dataIndex: 'expiry_date', width: 120 },
                { title: 'Status', dataIndex: 'status', width: 120 },
              ]}
            />
          </div>
          <div>
            <Text strong>Insurance</Text>
            <SimpleTable
              rows={insurance}
              columns={[
                { title: 'Type', dataIndex: 'insurance_type', width: 140 },
                { title: 'Insurer', dataIndex: 'insurer', width: 140 },
                { title: 'Policy #', dataIndex: 'policy_number', width: 140 },
                { title: 'Expiry', dataIndex: 'expiry_date', width: 120 },
                { title: 'Status', dataIndex: 'status', width: 120 },
              ]}
            />
          </div>
        </Space>
      ),
    },
    {
      key: 'final-account',
      label: 'Final Account',
      children: faQ.isError ? (
        <Alert type="warning" showIcon message={(faQ.error as Error)?.message} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {isActive && !(faQ.data || []).length ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={createFaM.isPending}
              onClick={() => createFaM.mutate()}
            >
              Create final account
            </Button>
          ) : null}
          <SimpleTable
            rows={(faQ.data || []) as Record<string, unknown>[]}
            columns={[
              { title: 'Number', dataIndex: 'account_number', width: 160 },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: 'Actions',
                dataIndex: 'id',
                width: 100,
                render: (_v, r) => (
                  <Button
                    type="link"
                    size="small"
                    icon={<PrinterOutlined />}
                    onClick={() => printFinalAccount(r)}
                  >
                    Print
                  </Button>
                ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'closeout',
      label: 'Closeout',
      children: closeQ.isError ? (
        <Alert type="warning" showIcon message={(closeQ.error as Error)?.message} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {isActive && !(closeQ.data || []).length ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={createCloseM.isPending}
              onClick={() => createCloseM.mutate()}
            >
              Create closeout
            </Button>
          ) : null}
          <SimpleTable
            rows={(closeQ.data || []) as Record<string, unknown>[]}
            columns={[
              { title: 'Number', dataIndex: 'closeout_number', width: 160 },
              { title: 'Status', dataIndex: 'status', width: 120 },
            ]}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/epc/commercial/subcontracts')}
        >
          Register
        </Button>
        <Button
          icon={<ControlOutlined />}
          onClick={() => navigate(`/epc/commercial/subcontracts/${id}/control`)}
        >
          Control
        </Button>
        {canEditParticulars ? (
          <Button
            type="primary"
            loading={submitAgrM.isPending}
            onClick={() => submitAgrM.mutate()}
          >
            Submit agreement
          </Button>
        ) : null}
      </Space>
      <Title level={3} style={{ marginBottom: 4 }}>
        {String(agr.agreement_number || 'Subcontract')} — workspace
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Buy-side commercial admin. SPC → certification → Draft SI → Finance PV. No GL from this
        module.
      </Text>
      <Tabs activeKey={tab} onChange={setTab} items={items} />

      {exceedanceProjectId ? (
        <BudgetExceedanceRequestModal
          open={exceedanceOpen}
          onClose={() => setExceedanceOpen(false)}
          projectId={exceedanceProjectId}
          sourceEntityType={exceedanceSourceType}
          sourceEntityId={exceedanceSourceId}
          lines={exceedanceLines}
          checkResult={exceedanceCheck as never}
          onSubmitted={() => {
            setExceedanceOpen(false);
            invalidate();
          }}
        />
      ) : null}

      <Drawer
        title="Edit particulars"
        width={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnClose
        extra={
          <Button type="primary" loading={patchAgrM.isPending} onClick={() => editForm.submit()}>
            Save
          </Button>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            patchAgrM.mutate({
              title: values.title,
              contractSum: values.contractSum != null ? Number(values.contractSum) : undefined,
              advancePct: values.advancePct != null ? Number(values.advancePct) : undefined,
              retentionPct: values.retentionPct != null ? Number(values.retentionPct) : undefined,
              attendancePct:
                values.attendancePct != null ? Number(values.attendancePct) : undefined,
              ldRatePerDay:
                values.ldRatePerDay != null ? Number(values.ldRatePerDay) : undefined,
              ldCap: values.ldCap != null ? Number(values.ldCap) : undefined,
              clauseProfile: values.clauseProfile,
            });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contractSum" label="Contract sum">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item name="advancePct" label="Advance %">
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="retentionPct" label="Retention %">
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="attendancePct" label="Attendance %">
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item name="ldRatePerDay" label="LD rate / day">
              <InputNumber min={0} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="ldCap" label="LD cap">
              <InputNumber min={0} style={{ width: 140 }} />
            </Form.Item>
          </Space>
          <Form.Item name="clauseProfile" label="Clause profile">
            <Select options={CLAUSE_PROFILE_OPTIONS} />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title="New Obligation"
        open={oblOpen}
        onCancel={() => setOblOpen(false)}
        onOk={() => oblForm.submit()}
        confirmLoading={createOblM.isPending}
        destroyOnClose
      >
        <Form
          form={oblForm}
          layout="vertical"
          initialValues={{ status: 'NOT_STARTED' }}
          onFinish={(values) => {
            createOblM.mutate({
              title: values.title,
              dueDate: ymd(values.dueDate as Dayjs | null),
              status: values.status || 'NOT_STARTED',
            });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="dueDate" label="Due date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={OBLIGATION_STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Variation"
        open={voOpen}
        onCancel={() => setVoOpen(false)}
        onOk={() => voForm.submit()}
        confirmLoading={createVoM.isPending}
        destroyOnClose
      >
        <Form
          form={voForm}
          layout="vertical"
          onFinish={(values) => {
            createVoM.mutate({
              agreementId: id,
              title: values.title,
              proposedValue: values.proposedValue != null ? Number(values.proposedValue) : 0,
              timeImpactDays:
                values.timeImpactDays != null ? Number(values.timeImpactDays) : undefined,
            });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="proposedValue" label="Proposed value" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="timeImpactDays" label="Time impact (days)">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Claim"
        open={claimOpen}
        onCancel={() => setClaimOpen(false)}
        onOk={() => claimForm.submit()}
        confirmLoading={createClaimM.isPending}
        destroyOnClose
      >
        <Form
          form={claimForm}
          layout="vertical"
          initialValues={{ claimDirection: 'SubToMain' }}
          onFinish={(values) => {
            createClaimM.mutate({
              agreementId: id,
              title: values.title,
              claimDirection: values.claimDirection,
              claimedAmount: values.claimedAmount != null ? Number(values.claimedAmount) : 0,
              currency: currency || 'AED',
            });
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="claimDirection" label="Direction" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'SubToMain', label: 'Sub → Main' },
                { value: 'MainToSub', label: 'Main → Sub' },
              ]}
            />
          </Form.Item>
          <Form.Item name="claimedAmount" label="Claimed amount" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New EOT Request"
        open={eotOpen}
        onCancel={() => setEotOpen(false)}
        onOk={() => eotForm.submit()}
        confirmLoading={createEotM.isPending}
        destroyOnClose
      >
        <Form
          form={eotForm}
          layout="vertical"
          onFinish={(values) => {
            const notes = values.notes ? String(values.notes).trim() : '';
            createEotM.mutate({
              agreementId: id,
              requestedDays: Number(values.requestedDays) || 0,
              title: notes || undefined,
              metaJson: notes ? { notes } : undefined,
            });
          }}
        >
          <Form.Item
            name="requestedDays"
            label="Requested days"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Security"
        open={secOpen}
        onCancel={() => setSecOpen(false)}
        onOk={() => secForm.submit()}
        confirmLoading={createSecM.isPending}
        destroyOnClose
      >
        <Form
          form={secForm}
          layout="vertical"
          onFinish={(values) => {
            createSecM.mutate({
              securityType: values.securityType,
              amount: values.amount != null ? Number(values.amount) : undefined,
              referenceNumber: values.referenceNumber,
              expiryDate: ymd(values.expiryDate as Dayjs | null),
              currency: currency || 'AED',
            });
          }}
        >
          <Form.Item name="securityType" label="Type" rules={[{ required: true }]}>
            <Select options={SECURITY_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="amount" label="Amount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="referenceNumber" label="Reference number">
            <Input />
          </Form.Item>
          <Form.Item name="expiryDate" label="Expiry date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Insurance"
        open={insOpen}
        onCancel={() => setInsOpen(false)}
        onOk={() => insForm.submit()}
        confirmLoading={createInsM.isPending}
        destroyOnClose
      >
        <Form
          form={insForm}
          layout="vertical"
          onFinish={(values) => {
            createInsM.mutate({
              insuranceType: values.insuranceType,
              insurer: values.insurer,
              policyNumber: values.policyNumber,
              expiryDate: ymd(values.expiryDate as Dayjs | null),
              currency: currency || 'AED',
            });
          }}
        >
          <Form.Item name="insuranceType" label="Type" rules={[{ required: true }]}>
            <Select options={INSURANCE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="insurer" label="Insurer">
            <Input />
          </Form.Item>
          <Form.Item name="policyNumber" label="Policy number">
            <Input />
          </Form.Item>
          <Form.Item name="expiryDate" label="Expiry date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <ConvertCertificationToSiModal
        open={Boolean(convertCertId)}
        confirming={convertSiM.isPending}
        defaultDepartmentId={
          agreement?.department_id ? String(agreement.department_id) : null
        }
        onCancel={() => setConvertCertId(null)}
        onConfirm={(payload) => {
          if (!convertCertId) return;
          convertSiM.mutate({ certId: convertCertId, ...payload });
        }}
      />
    </div>
  );
};

export default EpcSubcontractWorkspacePage;
